import { Box, Grid2 as Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { VersionTooltip } from '@/components/VersionTooltip';
import type { FieldVersionHistory } from '@/hooks/useVersionHistory';
import { DATE_FIELDS, REPORTED_CONSIDERATION_COMMENT_FIELD, SUMMARY_FIELDS } from '../constants';
import { styles } from '../styles';
import type { DealDetailsFormField, DealDetailsFormValue, DealDetailsFormValues } from '../types';
import { getSectionMetadata } from '../utils/getSectionMetadata';
import { FormSection } from './FormSection';

type SummarySectionProps = {
  values: DealDetailsFormValues;
  onChange: (field: DealDetailsFormField, value: DealDetailsFormValue) => void;
  errors?: Record<string, boolean>;
  versionHistory: Map<string, FieldVersionHistory> | undefined;
};

const SummarySectionWithProviders = ({
  values,
  onChange,
  errors = {},
  versionHistory,
}: SummarySectionProps) => {
  const sectionFields = [...DATE_FIELDS, ...SUMMARY_FIELDS, REPORTED_CONSIDERATION_COMMENT_FIELD];
  const metadata = getSectionMetadata(versionHistory, sectionFields);

  return (
    <FormSection title='Summary' metadata={metadata}>
      {/* Fields */}
      <Grid container spacing={2}>
        {DATE_FIELDS.map(field => (
          <Grid size={{ sm: 12, md: 4 }} key={field.key}>
            <VersionTooltip fieldHistory={versionHistory?.get(field.key)} fieldType={field.type}>
              <Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                    {field.label}
                  </Typography>
                </Box>
              </Box>
            </VersionTooltip>
            <DatePicker
              sx={styles.textField}
              format='DD MMM YYYY'
              value={values[field.key] ? dayjs(values[field.key]) : null}
              onChange={newValue => onChange(field, newValue)}
            />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {SUMMARY_FIELDS.map(field => (
          <Grid size={{ sm: 12, md: 4 }} key={field.key}>
            <VersionTooltip fieldHistory={versionHistory?.get(field.key)} fieldType={field.type}>
              <Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                    {field.label}
                  </Typography>
                </Box>
              </Box>
            </VersionTooltip>
            {field.type === 'select' ? (
              <Select
                sx={styles.select}
                fullWidth
                onChange={event => onChange(field, event.target.value)}
                value={values[field.key] || ''}
              >
                {field.options?.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <TextField
                sx={styles.textField}
                name={field.key}
                value={values[field.key] || ''}
                error={errors[field.key] || false}
                helperText={errors[field.key] && field.type === 'number' ? 'Must be a number' : ''}
                onChange={event => onChange(field, event.target.value)}
                fullWidth
              />
            )}
          </Grid>
        ))}
      </Grid>

      {/* Special case for Consideration Comment */}
      <VersionTooltip
        fieldHistory={versionHistory?.get(REPORTED_CONSIDERATION_COMMENT_FIELD.key)}
        fieldType={REPORTED_CONSIDERATION_COMMENT_FIELD.type}
      >
        <Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
              Consideration Comment
            </Typography>
          </Box>
        </Box>
      </VersionTooltip>
      <TextField
        sx={styles.textField}
        name={REPORTED_CONSIDERATION_COMMENT_FIELD.key}
        value={values.reportedConsiderationComment}
        onChange={event => onChange(REPORTED_CONSIDERATION_COMMENT_FIELD, event.target.value)}
        fullWidth
        multiline
        minRows={3}
      />
    </FormSection>
  );
};

export const SummarySection = ({
  values,
  onChange,
  errors = {},
  versionHistory,
}: SummarySectionProps) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <SummarySectionWithProviders
      values={values}
      onChange={onChange}
      errors={errors}
      versionHistory={versionHistory}
    />
  </LocalizationProvider>
);
